import { activeSeconds } from './game.js';
// FLOW Platform — Stats view.
//
// One source of truth for the stats card that pops up after a solve and
// that the header stats icon opens any time. Beta/test mode reads local
// counters from localStorage; real mode pulls /streak + /stats from the
// server. All language is "hints" (no stars).

const BETA_STATS_KEY = 'flow_fht_beta_stats';

function emptyBeta() {
  return { plays: 0, solves: 0, totalHintsUsed: 0, totalTime: 0 };
}

export function readBetaStats() {
  try {
    const raw = window.localStorage.getItem(BETA_STATS_KEY);
    if (!raw) return emptyBeta();
    const s = JSON.parse(raw) || {};
    return {
      plays: Number(s.plays) || 0,
      solves: Number(s.solves) || 0,
      totalHintsUsed: Number(s.totalHintsUsed) || 0,
      totalTime: Number(s.totalTime) || 0,
    };
  } catch (e) {
    return emptyBeta();
  }
}

// Record one completed play. Called once when the post-game screen mounts
// in beta/test mode. (Real mode is recorded server-side by POST /plays.)
export function recordBetaPlay(state) {
  try {
    const s = readBetaStats();
    s.plays += 1;
    if (state.status === 'solved') {
      s.solves += 1;
      const used = Math.max(0, (state.max_stars || 0) - (state.stars_remaining || 0));
      s.totalHintsUsed += used;
    }
    s.totalGuesses = (Number(s.totalGuesses) || 0) + (Number(state.guess_count) || 0);
    s.totalTime += activeSeconds(state);
    window.localStorage.setItem(BETA_STATS_KEY, JSON.stringify(s));
  } catch (e) { /* ignore */ }
}

function addRow(grid, label, value) {
  const dt = document.createElement('dt');
  dt.textContent = label;
  const dd = document.createElement('dd');
  dd.textContent = value;
  grid.appendChild(dt);
  grid.appendChild(dd);
}

function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m + ':' + (r < 10 ? '0' + r : String(r));
}

// Build the stats content node. Self-populates: synchronous for beta,
// asynchronous (fetch) for real mode.
export function buildStatsBody(opts) {
  opts = opts || {};
  const api = opts.api;
  const testMode = !!opts.testMode;

  const wrap = document.createElement('div');
  wrap.className = 'flow-fht-stats-sheet';

  if (testMode || !api) {
    const s = readBetaStats();
    const grid = document.createElement('dl');
    grid.className = 'flow-fht-stats-grid';
    addRow(grid, 'Puzzles played', String(s.plays));
    addRow(grid, 'Solved', String(s.solves));
    const avgHints = s.solves > 0 ? (s.totalHintsUsed / s.solves).toFixed(1) : '\u2014';
    addRow(grid, 'Avg hints used', avgHints);
    const avgTime = s.plays > 0 ? formatDuration(s.totalTime / s.plays) : '\u2014';
    addRow(grid, 'Avg time', avgTime);
    const tg = Number(s.totalGuesses) || 0;
    addRow(grid, 'Guesses', String(tg));
    addRow(grid, 'Avg guesses', s.plays > 0 ? (tg / s.plays).toFixed(1) : '\u2014');
    wrap.appendChild(grid);
    return wrap;
  }

  // Real mode — fetch from the server.
  const loading = document.createElement('p');
  loading.className = 'flow-fht-stats-loading';
  loading.textContent = 'Loading stats\u2026';
  wrap.appendChild(loading);

  Promise.all([
    api.get('games/fht/streak').then((r) => r.data).catch(() => null),
    api.get('games/fht/stats').then((r) => r.data).catch(() => null),
  ]).then(([streak, stats]) => {
    wrap.innerHTML = '';
    if (!streak && !stats) {
      const none = document.createElement('p');
      none.className = 'flow-fht-stats-loading';
      none.textContent = 'No stats yet \u2014 play a puzzle to get started.';
      wrap.appendChild(none);
      return;
    }
    const grid = document.createElement('dl');
    grid.className = 'flow-fht-stats-grid';
    if (streak) {
      addRow(grid, 'Current streak', String(streak.current_streak || 0));
      addRow(grid, 'Best streak', String(streak.longest_streak || 0));
    }
    if (stats) {
      addRow(grid, 'Plays', String(stats.plays_total || 0));
      const rate = stats.solve_rate;
      addRow(grid, 'Solve rate', (typeof rate === 'number') ? (Math.round(rate * 100) + '%') : '\u2014');
      const avg = stats.avg_time_seconds;
      addRow(grid, 'Avg solve time', (typeof avg === 'number' && avg > 0) ? formatDuration(avg) : '\u2014');
      if (typeof stats.guesses_total === 'number') {
        addRow(grid, 'Guesses', String(stats.guesses_total));
      }
      const ag = stats.avg_guesses;
      addRow(grid, 'Avg guesses', (typeof ag === 'number' && ag > 0) ? ag.toFixed(1) : '\u2014');
    }
    wrap.appendChild(grid);
  }).catch(() => {
    wrap.innerHTML = '';
    const err = document.createElement('p');
    err.className = 'flow-fht-stats-error';
    err.textContent = 'Couldn\u2019t load your stats. Try again.';
    wrap.appendChild(err);
  });

  return wrap;
}
